import { Component, inject, signal } from '@angular/core';
import { ErrorService } from '@services/error.service';
import { ExcelExportService } from '@services/excel-export.service';
import { ColDef, GridApi } from 'ag-grid-community';
import { BaseTranslatableComponent } from './base-translatable.component';

/**
 * Extended base component for data grid pages that require translation, Excel export, and error handling.
 * This component builds on BaseTranslatableComponent and adds grid-specific functionality.
 */
@Component({ template: '' })
export abstract class BaseDataGridComponent extends BaseTranslatableComponent {
  protected excelExportService = inject(ExcelExportService);
  protected errorService = inject(ErrorService);

  /**
   * Export ag-Grid data to Excel
   * @param gridApi - The ag-Grid API instance
   * @param columnDefs - Column definitions for the grid
   * @param fileName - Name for the exported file (without extension)
   * @param isExporting - Signal to track export state
   */
  protected async exportGridToExcel<T>(
    gridApi: GridApi<T>,
    columnDefs: ColDef<T>[],
    fileName: string,
    isExporting: ReturnType<typeof signal<boolean>>
  ): Promise<void> {
    if (!gridApi || isExporting()) return;

    isExporting.set(true);
    try {
      await this.excelExportService.exportGridToExcel(gridApi, columnDefs, fileName, this.lang());
    } catch (error) {
      console.error('Excel export failed:', error);
      this.errorService.showError('Export failed. Please try again.');
    } finally {
      isExporting.set(false);
    }
  }
}
